// Custom hook for ShopDetails business logic
// Architecture: All stateful/data logic for ShopDetails should go here.
import { useState, useMemo, useCallback, useEffect } from 'react';
import polyline from '@mapbox/polyline';
import { logEvent } from '../../../../utils/analytics';

export function useShopDetailsLogic(shopDetails) {
  const shop = shopDetails || {};
  const [map, setMap] = useState(null);
  const [directionsError, setDirectionsError] = useState("");
  const [requestingDirections, setRequestingDirections] = useState(false);
  const [routePath, setRoutePath] = useState([]);
  const [travelMode, setTravelMode] = useState(null);

  useEffect(() => {
    if (shop) {
      logEvent('shop_details_viewed', {
        shop_name: shop.shopName,
        shop_type: shop.shopType
      });
    }
  }, [shop]);

  const center = useMemo(() => ({
    lat: shop?.markerPosition?.lat || 0,
    lng: shop?.markerPosition?.lng || 0
  }), [shop?.markerPosition?.lat, shop?.markerPosition?.lng]);

  const onLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
    logEvent('map_loaded', { shop_name: shop.shopName });
    if (window.google && window.google.maps && window.google.maps.marker) {
      const advancedMarker = new window.google.maps.marker.AdvancedMarkerElement({
        position: center,
        map: mapInstance,
        content: document.createElement('div')
      });
      const markerDiv = advancedMarker.content;
      markerDiv.style.width = '20px';
      markerDiv.style.height = '20px';
      markerDiv.style.backgroundColor = 'red';
      markerDiv.style.borderRadius = '50%';
      markerDiv.style.border = '2px solid white';
    }
  }, [center, shop]);

  const handleGetDirections = useCallback(async (mode) => {
    setRoutePath([]);
    setDirectionsError("");
    setRequestingDirections(true);
    if (!navigator.geolocation) {
      setDirectionsError("Geolocation is not supported by your browser.");
      setRequestingDirections(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(async (position) => {
      const origin = {
        lat: position.coords.latitude,
        lng: position.coords.longitude
      };
      const destination = shop.markerPosition;
      let travelModeStr = 'DRIVE';
      if (mode === true) travelModeStr = 'TWO_WHEELER';
      if (mode === false) travelModeStr = 'WALK';
      try {
        const response = await fetch('https://routes.googleapis.com/directions/v2:computeRoutes', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Goog-Api-Key': process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
            'X-Goog-FieldMask': 'routes.polyline.encodedPolyline'
          },
          body: JSON.stringify({
            origin: { location: { latLng: { latitude: origin.lat, longitude: origin.lng } } },
            destination: { location: { latLng: { latitude: destination.lat, longitude: destination.lng } } },
            travelMode: travelModeStr
          })
        });
        const data = await response.json();
        if (data.routes && data.routes[0] && data.routes[0].polyline && data.routes[0].polyline.encodedPolyline) {
          const decodedPath = polyline.decode(data.routes[0].polyline.encodedPolyline)
            .map(([lat, lng]) => ({ lat, lng }));
          setRoutePath(decodedPath);
        } else {
          setDirectionsError("No route found.");
        }
      } catch (err) {
        setDirectionsError("Could not fetch directions. Try again later.");
      }
      setRequestingDirections(false);
    }, (error) => {
      setDirectionsError("Unable to access your location.");
      setRequestingDirections(false);
    });
  }, [shop]);

  useEffect(() => {
    if (!shop.markerPosition) return;
    if (travelMode === null) {
      setRoutePath([]);
      setDirectionsError("");
      return;
    }
    handleGetDirections(travelMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [travelMode, shop.markerPosition]);

  useEffect(() => {
    if (map && routePath.length > 0 && window.google && window.google.maps) {
      const bounds = new window.google.maps.LatLngBounds();
      routePath.forEach(point => bounds.extend(point));
      map.fitBounds(bounds);
    }
  }, [routePath, map]);

  return {
    map,
    setMap,
    directionsError,
    setDirectionsError,
    requestingDirections,
    setRequestingDirections,
    routePath,
    setRoutePath,
    travelMode,
    setTravelMode,
    center,
    onLoad,
    handleGetDirections
  };
}
